<!-- ReactivateSubscriptionConfirmModal.vue
     Modal de confirmação para reativar uma assinatura recorrente
     que está atualmente pausada no Mercado Pago.
     ─────────────────────────────────────────────────────────────
     Casos de uso:
     - A assinatura está com preapprovalStatus = "paused" no MP.
       O usuário mudou de ideia e quer retomar a renovação
       automática antes do fim do período corrente.
     - No backend, o preapproval é alterado de "paused" para
       "authorized". A próxima cobrança ocorre no next_payment_date
       já existente.
-->

<script setup>
import { onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  isReactivating: {
    type: Boolean,
    default: false,
  },
  // Data formatada (dd/MM/yyyy) até quando o ciclo atual vai.
  // É a partir dessa data que a próxima cobrança ocorre.
  endDateFormatted: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["close", "confirm"]);

function close() {
  if (props.isReactivating) return;
  emit("close");
}

function confirm() {
  if (props.isReactivating) return;
  emit("confirm");
}

function handleEsc(e) {
  if (e.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEsc);
});
</script>

<template>
  <transition name="modal-fade" appear>
    <div
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reactivate-sub-title"
      @click.self="close"
    >
      <div class="modal-panel modal-panel--compact">
        <div class="modal-panel__icon">
          <i class="fas fa-play-circle"></i>
        </div>

        <h4 id="reactivate-sub-title" class="modal-panel__title">
          Reativar renovação?
        </h4>

        <p class="modal-panel__body">
          Sua assinatura voltará a ser renovada automaticamente.
          <template v-if="endDateFormatted">
            A próxima cobrança ocorrerá em
            <strong>{{ endDateFormatted }}</strong>, ao fim do período
            atual.
          </template>
          <template v-else>
            A próxima cobrança ocorrerá ao fim do período atual.
          </template>
        </p>

        <p class="modal-panel__body modal-panel__body--small">
          Você pode pausar novamente a qualquer momento se mudar de
          ideia.
        </p>

        <div class="modal-panel__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isReactivating"
            @click="close"
          >
            Voltar
          </button>
          <button
            type="button"
            class="btn btn-reactivate"
            :disabled="isReactivating"
            @click="confirm"
          >
            <span v-if="isReactivating" class="btn-spinner"></span>
            <span>{{
              isReactivating ? "Reativando..." : "Confirmar reativação"
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--success-bg);
  border: 1px solid #86efac;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #15803d;
  font-size: 1.4rem;
  margin-bottom: 0.25rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
}

.modal-panel__body--small {
  font-size: 0.82rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: var(--success-text);
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  line-height: 1.45;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.btn-reactivate {
  background-color: var(--success-bg);
  color: #15803d;
  border-color: #86efac;
}

.btn-reactivate:hover:not(:disabled) {
  background-color: #bbf7d0;
}

.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid #bbf7d0;
  border-top-color: #15803d;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}
</style>